import { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import {
  type PendingManagerHubVisitLogEvent,
  useRecordPendingManagerHubVisit as useRecordPendingManagerHubVisitShared,
} from '@lyrahealth-inc/shared-app-logic/src/features/managerHub/hooks/useRecordPendingManagerHubVisit'
import type { PutUserPropertyRequest } from '@lyrahealth-inc/shared-app-logic/src/models/user/User'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'

import { getDisableSelfCareTopics, getLibraryDisabled } from '../../data/customer/customerSelectors'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import { putUserProperty } from '../../data/user/userActions'
import {
  getId,
  getIsUserLoggedIn,
  getIsUserMinor,
  getManagerHubPromotionSignal,
  getUserDisplayLanguageOrDefault,
} from '../../data/user/userSelectors'
import { logToSentry } from '../utils/userUtils'

export const useRecordPendingManagerHubVisit = () => {
  const dispatch = useAppDispatch()
  const { isManagerHubEnabled } = useFlags()
  const { productType } = useContext(ProductTypeContext)
  const isUserLoggedIn = useSelector(getIsUserLoggedIn)
  const lyraId = useSelector(getId)
  const isLibraryDisabled = useSelector(getLibraryDisabled)

  const writePromotionSignal = useCallback(
    (lyraId: string, requestData: PutUserPropertyRequest) =>
      dispatch(putUserProperty({ lyraId, requestData })).unwrap(),
    [dispatch],
  )
  const logEvent = useCallback(
    ({ message, extras, error }: PendingManagerHubVisitLogEvent) =>
      logToSentry('ManagerHub', {
        userId: lyraId,
        message,
        type: error ? 'error' : 'info',
        source: 'useRecordPendingManagerHubVisit.ts',
        ...extras,
        ...(error ? { error } : {}),
      }),
    [lyraId],
  )

  useRecordPendingManagerHubVisitShared({
    isReady: isUserLoggedIn,
    lyraId,
    isManagerHubEnabled,
    productType,
    isUserMinor: useSelector(getIsUserMinor),
    language: useSelector(getUserDisplayLanguageOrDefault),
    isEssentialsEnabled: !isLibraryDisabled,
    disableSelfCareTopics: useSelector(getDisableSelfCareTopics),
    managerHubPromotionSignal: useSelector(getManagerHubPromotionSignal),
    writePromotionSignal,
    log: logEvent,
  })
}
